export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  condition: (gameData: GameData) => boolean;
  unlockedAt?: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  level?: number; // För level-baserade achievements
  maxLevel?: number; // Max level för detta achievement
  experience?: number; // Nuvarande XP för level
  experienceRequired?: number; // XP som krävs för nästa level
}

export interface GameData {
  scores: any;
  totalScore: number;
  dice: any[];
  rollsUsed: number;
  totalRolls?: number;
  currentRound: number;
  gameHistory?: GameStats[];
  modeId?: string; // classic | maxi | speed | etc
  gameStartTime?: number; // Timestamp när spelet började
  rollTimes?: number[]; // Timestamps för varje kast
  firstRollAchieved?: boolean; // Om spelaren fick bra resultat på första kastet
  streakData?: StreakData; // Streak information
}

export interface GameStats {
  totalScore: number;
  yatzyCount: number;
  perfectGame: boolean;
  date: string;
  playTime?: number; // Speltid i millisekunder
  averageRollTime?: number; // Genomsnittlig tid per kast
}

export interface StreakData {
  currentWinStreak: number; // Nuvarande vinst-streak (över viss poäng)
  longestWinStreak: number; // Längsta vinst-streak
  currentPlayStreak: number; // Nuvarande spel-streak (dagar i rad)
  longestPlayStreak: number; // Längsta spel-streak
  lastPlayDate: string; // Senaste speldatum
}

// Base (mode-agnostic) achievements for classic
export const achievements: Achievement[] = [
  // Grundläggande achievements
  {
    id: 'first-game',
    name: 'Första kastet',
    description: 'Slutför ditt första spel',
    icon: '🎲',
    rarity: 'common',
  condition: (data) => data.currentRound > 15
  },
  {
    id: 'century-club',
    name: 'Hundraklubben',
    description: 'Få 100 poäng eller mer',
    icon: '💯',
    rarity: 'common',
    condition: (data) => data.totalScore >= 100
  },
  {
    id: 'double-century',
    name: 'Dubbelsekel',
    description: 'Få 200 poäng eller mer',
    icon: '🔥',
    rarity: 'rare',
    condition: (data) => data.totalScore >= 200
  },
  {
    id: 'triple-century',
    name: 'Trippelsekel',
    description: 'Få 300 poäng eller mer',
    icon: '⭐',
    rarity: 'epic',
    condition: (data) => data.totalScore >= 300
  },

  // Yatzy-specifika achievements
  {
    id: 'first-yatzy',
    name: 'Yatzy-mästare',
    description: 'Få din första Yatzy (5 lika)',
    icon: '🏆',
    rarity: 'rare',
    condition: (data) => data.scores?.yatzy > 0
  },
  {
    id: 'yatzy-collector',
    name: 'Yatzy-samlare',
    description: 'Få 10 Yatzy totalt',
    icon: '🏅',
    rarity: 'epic',
    condition: (data) => {
      const history = data.gameHistory || [];
      const totalYatzys = history.reduce((sum, game) => sum + (game.yatzyCount || 0), 0);
      return totalYatzys >= 10;
    }
  },
  {
    id: 'yatzy-legend',
    name: 'Yatzy-legend',
    description: 'Få 100 Yatzy totalt',
    icon: '👑',
    rarity: 'legendary',
    condition: (data) => {
      const history = data.gameHistory || [];
      const totalYatzys = history.reduce((sum, game) => sum + (game.yatzyCount || 0), 0);
      return totalYatzys >= 100;
    }
  },

  // Effektivitets-achievements
  {
    id: 'efficient-roller',
    name: 'Effektiv kastare',
    description: 'Fyll en kategori med bara ett kast',
    icon: '🎯',
    rarity: 'rare',
    condition: (data) => data.rollsUsed === 1
  },
  {
    id: 'perfect-round',
    name: 'Perfekt runda',
    description: 'Använd exakt 3 kast i en runda',
    icon: '⚡',
    rarity: 'common',
    condition: (data) => data.rollsUsed === 3
  },

  // Bonus-achievements
  {
    id: 'bonus-master',
    name: 'Bonusmästare',
    description: 'Få bonus från övre sektionen (63+ poäng)',
    icon: '🎁',
    rarity: 'common',
    condition: (data) => {
      if (!data || !data.scores) return false;
      const upperScore = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes']
        .reduce((sum, key) => sum + (data.scores[key] || 0), 0);
      return upperScore >= 63;
    }
  },
  {
    id: 'upper-section-master',
    name: 'Övre sektionens mästare',
    description: 'Få mer än 80 poäng i övre sektionen',
    icon: '🔝',
    rarity: 'rare',
    condition: (data) => {
      if (!data || !data.scores) return false;
      const upperScore = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes']
        .reduce((sum, key) => sum + (data.scores[key] || 0), 0);
      return upperScore > 80;
    }
  },
  {
    id: 'max-bonus',
    name: 'Maximal bonus',
    description: 'Få maximal poäng i övre sektionen (126 poäng)',
    icon: '💎',
    rarity: 'epic',
    condition: (data) => {
      if (!data || !data.scores) return false;
      const upperScore = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes']
        .reduce((sum, key) => sum + (data.scores[key] || 0), 0);
      return upperScore === 126; // 6×1 + 12×2 + 18×3 + 24×4 + 30×5 + 36×6 = 126
    }
  },

  // Stege-achievements
  {
    id: 'small-straight-master',
    name: 'Liten stege-mästare',
    description: 'Få liten stege (1-2-3-4-5)',
    icon: '📈',
    rarity: 'common',
    condition: (data) => data.scores?.smallStraight === 15
  },
  {
    id: 'large-straight-master',
    name: 'Stor stege-mästare',
    description: 'Få stor stege (2-3-4-5-6)',
    icon: '📊',
    rarity: 'rare',
    condition: (data) => data.scores?.largeStraight === 20
  },

  // Kombinations-achievements
  {
    id: 'full-house-expert',
    name: 'Kåk-expert',
    description: 'Få kåk (triss + par)',
    icon: '🏠',
    rarity: 'common',
    condition: (data) => data.scores?.fullHouse > 0
  },
  {
    id: 'four-of-a-kind',
    name: 'Fyrtal-hjälte',
    description: 'Få fyrtal (fyra lika)',
    icon: '🔷',
    rarity: 'rare',
    condition: (data) => data.scores?.fourOfAKind > 0
  },

  // Speciella achievements
  {
    id: 'lucky-seven',
    name: 'Lyckliga sjuan',
    description: 'Spela 7 spel',
    icon: '🍀',
    rarity: 'common',
    condition: (data) => (data.gameHistory?.length || 0) >= 7
  },
  {
    id: 'game-veteran',
    name: 'Spelsveteran',
    description: 'Spela 50 spel',
    icon: '🎖️',
    rarity: 'rare',
    condition: (data) => (data.gameHistory?.length || 0) >= 50
  },
  {
    id: 'game-master',
    name: 'Spelmästare',
    description: 'Spela 200 spel',
    icon: '🏆',
    rarity: 'epic',
    condition: (data) => (data.gameHistory?.length || 0) >= 200
  },
  {
    id: 'consistency-king',
    name: 'Konsistenskungen',
    description: 'Få över 150 poäng i 3 spel i rad',
    icon: '👨‍👑',
    rarity: 'epic',
    condition: (data) => {
      const recent = data.gameHistory?.slice(-3) || [];
      return recent.length === 3 && recent.every(game => game.totalScore >= 150);
    }
  },
  {
    id: 'perfectionist',
    name: 'Perfektionist',
    description: 'Fyll alla kategorier utan att få 0 poäng i någon',
    icon: '✨',
    rarity: 'legendary',
    condition: (data) => {
      const scores = data.scores;
      if (!scores) return false;
      const allFilled = Object.values(scores).every(score => score !== null);
      const noZeros = Object.values(scores).every(score => score !== 0);
      return allFilled && noZeros;
    }
  },

  // === NYA ACHIEVEMENTS V1.3.0 ===
  
  // Streak Achievements
  {
    id: 'win-streak-3',
    name: 'Hattrick',
    description: 'Vinn 3 spel i rad (150+ poäng)',
    icon: '🔥',
    rarity: 'rare',
    condition: (data) => (data.streakData?.currentWinStreak || 0) >= 3
  },
  {
    id: 'win-streak-5',
    name: 'Femma i rad',
    description: 'Vinn 5 spel i rad (150+ poäng)',
    icon: '🔥🔥',
    rarity: 'epic',
    condition: (data) => (data.streakData?.currentWinStreak || 0) >= 5
  },
  {
    id: 'win-streak-10',
    name: 'Oberörlig',
    description: 'Vinn 10 spel i rad (150+ poäng)',
    icon: '🔥🔥🔥',
    rarity: 'legendary',
    condition: (data) => (data.streakData?.currentWinStreak || 0) >= 10
  },
  {
    id: 'daily-streak-7',
    name: 'Veckoregular',
    description: 'Spela 7 dagar i rad',
    icon: '📅',
    rarity: 'rare',
    condition: (data) => (data.streakData?.currentPlayStreak || 0) >= 7
  },
  {
    id: 'daily-streak-30',
    name: 'Månadsutmaning',
    description: 'Spela 30 dagar i rad',
    icon: '📆',
    rarity: 'epic',
    condition: (data) => (data.streakData?.currentPlayStreak || 0) >= 30
  },

  // Hastighets Achievements
  {
    id: 'speed-demon',
    name: 'Hastighetsdemonen',
    description: 'Slutför ett spel på under 3 minuter',
    icon: '⚡',
    rarity: 'rare',
    condition: (data) => {
      if (!data.gameStartTime) return false;
      const playTime = Date.now() - data.gameStartTime;
      return data.currentRound > 15 && playTime < 180000; // 3 minuter
    }
  },
  {
    id: 'lightning-fast',
    name: 'Blixtsnabb',
    description: 'Slutför ett spel på under 2 minuter',
    icon: '⚡⚡',
    rarity: 'epic',
    condition: (data) => {
      if (!data.gameStartTime) return false;
      const playTime = Date.now() - data.gameStartTime;
      return data.currentRound > 15 && playTime < 120000; // 2 minuter
    }
  },
  {
    id: 'quick-roller',
    name: 'Snabbkastare',
    description: 'Genomsnitt under 5 sekunder per kast',
    icon: '🎯',
    rarity: 'rare',
    condition: (data) => {
      if (!data.rollTimes || data.rollTimes.length < 2) return false;
      const totalTime = data.rollTimes[data.rollTimes.length - 1] - data.rollTimes[0];
      const averageTime = totalTime / (data.rollTimes.length - 1);
      return averageTime < 5000; // 5 sekunder
    }
  },

  // Första kastet Achievements
  {
    id: 'first-roll-yatzy',
    name: 'Första kastet Yatzy!',
    description: 'Få Yatzy på första kastet',
    icon: '🎲✨',
    rarity: 'legendary',
    condition: (data) => {
      return data.rollsUsed === 1 && data.scores?.yatzy > 0;
    }
  },
  {
    id: 'first-roll-large-straight',
    name: 'Första kastet stege',
    description: 'Få stor stege på första kastet',
    icon: '📊💨',
    rarity: 'epic',
    condition: (data) => {
      return data.rollsUsed === 1 && data.scores?.largeStraight === 20;
    }
  },
  {
    id: 'first-roll-full-house',
    name: 'Första kastet kåk',
    description: 'Få kåk på första kastet',
    icon: '🏠💨',
    rarity: 'rare',
    condition: (data) => {
      return data.rollsUsed === 1 && data.scores?.fullHouse > 0;
    }
  },

  // Level-baserade Achievements (Progression)
  {
    id: 'dice-master-1',
    name: 'Tärningspadawan',
    description: 'Spela 5 spel',
    icon: '🎲',
    rarity: 'common',
    level: 1,
    maxLevel: 10,
    condition: (data) => (data.gameHistory?.length || 0) >= 5
  },
  {
    id: 'dice-master-2',
    name: 'Tärningsriddare',
    description: 'Spela 15 spel',
    icon: '🎲⚔️',
    rarity: 'common',
    level: 2,
    maxLevel: 10,
    condition: (data) => (data.gameHistory?.length || 0) >= 15
  },
  {
    id: 'dice-master-3',
    name: 'Tärningskrigare',
    description: 'Spela 30 spel',
    icon: '🎲🛡️',
    rarity: 'rare',
    level: 3,
    maxLevel: 10,
    condition: (data) => (data.gameHistory?.length || 0) >= 30
  },
  {
    id: 'dice-master-4',
    name: 'Tärningsexpert',
    description: 'Spela 60 spel',
    icon: '🎲🏆',
    rarity: 'rare',
    level: 4,
    maxLevel: 10,
    condition: (data) => (data.gameHistory?.length || 0) >= 60
  },
  {
    id: 'dice-master-5',
    name: 'Tärningslegend',
    description: 'Spela 100 spel',
    icon: '🎲👑',
    rarity: 'epic',
    level: 5,
    maxLevel: 10,
    condition: (data) => (data.gameHistory?.length || 0) >= 100
  },

  // Poäng Level Achievements
  {
    id: 'score-hunter-1',
    name: 'Poängjägare I',
    description: 'Få över 180 poäng',
    icon: '🎯',
    rarity: 'common',
    level: 1,
    maxLevel: 5,
    condition: (data) => data.totalScore >= 180
  },
  {
    id: 'score-hunter-2',
    name: 'Poängjägare II',
    description: 'Få över 220 poäng',
    icon: '🎯🎯',
    rarity: 'rare',
    level: 2,
    maxLevel: 5,
    condition: (data) => data.totalScore >= 220
  },
  {
    id: 'score-hunter-3',
    name: 'Poängjägare III',
    description: 'Få över 260 poäng',
    icon: '🎯🎯🎯',
    rarity: 'epic',
    level: 3,
    maxLevel: 5,
    condition: (data) => data.totalScore >= 260
  },
  {
    id: 'score-hunter-4',
    name: 'Poängjägare IV',
    description: 'Få över 300 poäng',
    icon: '🎯👑',
    rarity: 'epic',
    level: 4,
    maxLevel: 5,
    condition: (data) => data.totalScore >= 300
  },
  {
    id: 'score-hunter-5',
    name: 'Poängjägare V - Ultimata',
    description: 'Få över 350 poäng',
    icon: '🎯💎',
    rarity: 'legendary',
    level: 5,
    maxLevel: 5,
    condition: (data) => data.totalScore >= 350
  },

  // Roliga/Unika Achievements
  {
    id: 'lucky-seven-scores',
    name: 'Sjuans magi',
    description: 'Få exakt 77, 177 eller 277 poäng',
    icon: '🍀',
    rarity: 'rare',
    condition: (data) => [77, 177, 277].includes(data.totalScore)
  },
  {
    id: 'perfect-hundred',
    name: 'Perfekt hundra',
    description: 'Få exakt 100, 200 eller 300 poäng',
    icon: '💯',
    rarity: 'rare',
    condition: (data) => [100, 200, 300].includes(data.totalScore)
  },
  {
    id: 'night-owl',
    name: 'Nattsuggla',
    description: 'Spela mellan 00:00 och 06:00',
    icon: '🦉',
    rarity: 'rare',
    condition: (data) => {
      const hour = new Date().getHours();
      return data.currentRound > 15 && hour >= 0 && hour < 5;
    }
  },
  {
    id: 'early-bird',
    name: 'Morgonpigg',
    description: 'Spela mellan 05:00 och 08:00',
    icon: '🐦',
    rarity: 'common',
    condition: (data) => {
      const hour = new Date().getHours();
      return data.currentRound > 15 && hour >= 5 && hour < 8;
    }
  },
  {
    id: 'weekend-warrior',
    name: 'Helgkrigare',
    description: 'Spela på lördag eller söndag',
    icon: '🎮',
    rarity: 'common',
    condition: (data) => {
      const day = new Date().getDay();
      return data.currentRound > 15 && (day === 0 || day === 6);
    }
  },
  {
    id: 'reverse-psychology',
    name: 'Omvänd psykologi',
    description: 'Få 13 poäng eller mindre (men slutför spelet)',
    icon: '🔄',
    rarity: 'rare',
    condition: (data) => data.currentRound > 15 && data.totalScore <= 13
  },
  {
    id: 'yatzy-collector-advanced',
    name: 'Yatzy-samlare Pro',
    description: 'Få 3 Yatzy totalt',
    icon: '🏅🏅🏅',
    rarity: 'legendary',
    condition: (data) => {
      const history = data.gameHistory || [];
      const totalYatzys = history.reduce((sum, game) => sum + (game.yatzyCount || 0), 0);
      const currentYatzy = data.scores?.yatzy === 50 ? 1 : 0;
      return totalYatzys + currentYatzy >= 3;
    }
  },
  {
    id: 'minimalist',
    name: 'Minimalist',
    description: 'Vinn med minsta möjliga antal kast (15 kast totalt)',
    icon: '🎲📉',
    rarity: 'epic',
    condition: (data) => {
      return data.currentRound > 15 && data.totalRolls === 15;
    }
  }
];

// Streak helper functions
export const streakHelpers = {
  updateStreakData: (gameData: GameData, isWin: boolean): StreakData => {
    const today = new Date().toISOString().split('T')[0];
    const existing = gameData.streakData || {
      currentWinStreak: 0,
      longestWinStreak: 0,
      currentPlayStreak: 0,
      longestPlayStreak: 0,
      lastPlayDate: ''
    };

    // Update play streak
    if (existing.lastPlayDate === today) {
      // Same day, keep current streak
    } else {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      if (existing.lastPlayDate === yesterdayStr) {
        // Consecutive day
        existing.currentPlayStreak += 1;
      } else {
        // Streak broken
        existing.currentPlayStreak = 1;
      }
    }

    // Update win streak
    if (isWin) {
      existing.currentWinStreak += 1;
      existing.longestWinStreak = Math.max(existing.longestWinStreak, existing.currentWinStreak);
    } else {
      existing.currentWinStreak = 0;
    }

    existing.longestPlayStreak = Math.max(existing.longestPlayStreak, existing.currentPlayStreak);
    existing.lastPlayDate = today;

    return existing;
  },

  loadStreakData: (): StreakData => {
    try {
      const stored = localStorage.getItem('yatzy-streak-data');
      return stored ? JSON.parse(stored) : {
        currentWinStreak: 0,
        longestWinStreak: 0,
        currentPlayStreak: 0,
        longestPlayStreak: 0,
        lastPlayDate: ''
      };
    } catch {
      return {
        currentWinStreak: 0,
        longestWinStreak: 0,
        currentPlayStreak: 0,
        longestPlayStreak: 0,
        lastPlayDate: ''
      };
    }
  },

  saveStreakData: (streakData: StreakData): void => {
    try {
      localStorage.setItem('yatzy-streak-data', JSON.stringify(streakData));
    } catch (error) {
      console.error('Error saving streak data:', error);
    }
  }
};

class AchievementManager {
  private unlockedAchievements: Set<string>;
  private modeId: string;

  constructor(modeId: string = 'classic') {
    this.modeId = modeId;
    this.unlockedAchievements = this.loadUnlockedAchievements();
  }

  setMode(modeId: string) {
    this.modeId = modeId;
    this.unlockedAchievements = this.loadUnlockedAchievements();
  }

  // Ny funktion för att förbereda gameData med streak-information
  enhanceGameData(gameData: GameData): GameData {
    const enhanced = { ...gameData };
    
    // Lägg till streak-data om det inte finns
    if (!enhanced.streakData) {
      enhanced.streakData = streakHelpers.loadStreakData();
    }
    
    // Lägg till gameStartTime om det inte finns
    if (!enhanced.gameStartTime && enhanced.currentRound <= 2) {
      enhanced.gameStartTime = Date.now();
    }
    
    return enhanced;
  }

  // Ny funktion för att uppdatera streak-data efter spel
  updateStreakData(gameData: GameData): GameData {
    const isWin = gameData.totalScore >= 150; // Definierar "vinst" som 150+ poäng
    const updatedStreakData = streakHelpers.updateStreakData(gameData, isWin);
    
    streakHelpers.saveStreakData(updatedStreakData);
    
    return {
      ...gameData,
      streakData: updatedStreakData
    };
  }

  private unlockTimestamps: Map<string, string> = new Map();

  private loadUnlockedAchievements(): Set<string> {
    try {
      const stored = localStorage.getItem(`yatzy-achievements:${this.modeId}`);
      const data = stored ? JSON.parse(stored) : [];
      // Support both old format (string[]) and new format ({id, unlockedAt}[])
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
        // Old format — migrate: no timestamps available
        return new Set(data);
      }
      // New format
      const ids = new Set<string>();
      for (const entry of data) {
        if (typeof entry === 'object' && entry.id) {
          ids.add(entry.id);
          if (entry.unlockedAt) {
            this.unlockTimestamps.set(entry.id, entry.unlockedAt);
          }
        }
      }
      return ids;
    } catch {
      return new Set();
    }
  }

  private saveUnlockedAchievements(): void {
    try {
      const achievements = Array.from(this.unlockedAchievements).map(id => ({
        id,
        unlockedAt: this.unlockTimestamps.get(id) || new Date().toISOString()
      }));
      localStorage.setItem(`yatzy-achievements:${this.modeId}`, JSON.stringify(achievements));
    } catch (error) {
      console.error('Error saving achievements:', error);
    }
  }

  checkAchievements(gameData: GameData): Achievement[] {
    const newAchievements: Achievement[] = [];
    
    // Förbättra gameData med streak-information och andra nya fält
    const enhancedGameData = this.enhanceGameData(gameData);

  for (const achievement of this.getAvailableAchievements()) {
      if (!this.unlockedAchievements.has(achievement.id) && achievement.condition(enhancedGameData)) {
        this.unlockedAchievements.add(achievement.id);
        const now = new Date().toISOString();
        this.unlockTimestamps.set(achievement.id, now);
        newAchievements.push({
          ...achievement,
          unlockedAt: now
        });
      }
    }

    if (newAchievements.length > 0) {
      this.saveUnlockedAchievements();
    }

    return newAchievements;
  }

  getUnlockedAchievements(): Achievement[] {
  return this.getAvailableAchievements()
      .filter(achievement => this.unlockedAchievements.has(achievement.id))
      .map(achievement => ({
        ...achievement,
        unlockedAt: this.unlockTimestamps.get(achievement.id) || new Date().toISOString()
      }));
  }

  getAllAchievements(): Achievement[] {
  return this.getAvailableAchievements().map(achievement => ({
      ...achievement,
      unlockedAt: this.unlockedAchievements.has(achievement.id)
        ? (this.unlockTimestamps.get(achievement.id) || new Date().toISOString())
        : undefined
    }));
  }

  getProgress(): { unlocked: number; total: number; percentage: number } {
    const unlocked = this.unlockedAchievements.size;
  const total = this.getAvailableAchievements().length;
    return {
      unlocked,
      total,
      percentage: Math.round((unlocked / total) * 100)
    };
  }

  resetAchievements(): void {
    this.unlockedAchievements.clear();
    localStorage.removeItem(`yatzy-achievements:${this.modeId}`);
  }

  // Nya funktioner för level-baserade achievements
  getLevelProgress(achievementSeries: string): { currentLevel: number; maxLevel: number; progress: number } {
    const seriesAchievements = this.getAvailableAchievements()
      .filter(a => a.id.startsWith(achievementSeries))
      .sort((a, b) => (a.level || 0) - (b.level || 0));
    
    let currentLevel = 0;
    let maxLevel = 0;
    
    for (const achievement of seriesAchievements) {
      maxLevel = Math.max(maxLevel, achievement.maxLevel || 1);
      if (this.unlockedAchievements.has(achievement.id)) {
        currentLevel = achievement.level || 1;
      }
    }
    
    const progress = maxLevel > 0 ? (currentLevel / maxLevel) * 100 : 0;
    
    return { currentLevel, maxLevel, progress };
  }

  getAchievementsByCategory(): { [category: string]: Achievement[] } {
    const allAchievements = this.getAllAchievements();
    const categories: { [category: string]: Achievement[] } = {
      streak: [],
      speed: [],
      'first-roll': [],
      levels: [],
      fun: [],
      classic: []
    };

    for (const achievement of allAchievements) {
      if (achievement.id.includes('streak')) {
        categories.streak.push(achievement);
      } else if (achievement.id.includes('speed') || achievement.id.includes('quick') || achievement.id.includes('lightning')) {
        categories.speed.push(achievement);
      } else if (achievement.id.includes('first-roll')) {
        categories['first-roll'].push(achievement);
      } else if (achievement.level && achievement.maxLevel) {
        categories.levels.push(achievement);
      } else if (achievement.id.includes('lucky') || achievement.id.includes('night') || achievement.id.includes('early') || 
                 achievement.id.includes('weekend') || achievement.id.includes('reverse') || achievement.id.includes('minimalist')) {
        categories.fun.push(achievement);
      } else {
        categories.classic.push(achievement);
      }
    }

    return categories;
  }

  // Förbättrad progress-funktion med kategorier
  getDetailedProgress(): {
    overall: { unlocked: number; total: number; percentage: number };
    categories: { [category: string]: { unlocked: number; total: number; percentage: number } };
    levelSeries: { [series: string]: { currentLevel: number; maxLevel: number; progress: number } };
  } {
    const categorized = this.getAchievementsByCategory();
    const overall = this.getProgress();
    
    const categoryProgress: { [category: string]: { unlocked: number; total: number; percentage: number } } = {};
    
    for (const [category, achievements] of Object.entries(categorized)) {
      const unlocked = achievements.filter(a => this.unlockedAchievements.has(a.id)).length;
      const total = achievements.length;
      const percentage = total > 0 ? Math.round((unlocked / total) * 100) : 0;
      categoryProgress[category] = { unlocked, total, percentage };
    }

    const levelSeries = {
      'dice-master': this.getLevelProgress('dice-master'),
      'score-hunter': this.getLevelProgress('score-hunter')
    };

    return {
      overall,
      categories: categoryProgress,
      levelSeries
    };
  }

  private getAvailableAchievements(): Achievement[] {
    switch (this.modeId) {
      case 'maxi':
        return [...achievements, ...maxiAchievements];
      default:
        return achievements;
    }
  }
}

export const achievementManager = new AchievementManager('classic');

// Helper: Maxi-specific achievements list (used by condition via modeId)
export const maxiAchievements: Achievement[] = [
  {
    id: 'maxi-first-yatzy',
    name: 'Maxi Yatzy',
    description: 'Få Yatzy i Maxi (5 eller 6 lika)',
    icon: '🎖️',
    rarity: 'rare',
    condition: (data) => (data.modeId === 'maxi') && (data.scores?.yatzy === 50 || data.scores?.yatzy === 100)
  },
  {
    id: 'maxi-six-kind',
    name: 'Sex lika!',
    description: 'Få 6 lika i Maxi',
    icon: '🧊',
    rarity: 'epic',
    condition: (data) => data.modeId === 'maxi' && data.scores?.yatzy === 100
  },
  {
    id: 'maxi-upper-bonus',
    name: 'Maxi Bonus',
    description: 'Få 84+ i övre sektionen (Maxi)',
    icon: '🎁',
    rarity: 'common',
    condition: (data) => {
      if (data.modeId !== 'maxi') return false;
      const upperScore = ['ones','twos','threes','fours','fives','sixes']
        .reduce((s, k) => s + (data.scores[k] || 0), 0);
      return upperScore >= 84;
    }
  }
];
